import { useAnimation, motion } from "framer-motion";
import { FC, useEffect } from "react";

export const AiIcon: FC = () => {
  const particleControls = useAnimation();
  const mainCircleControls = useAnimation();
  const brainControls = useAnimation();

  const startNormalAnimations = () => {
    particleControls.start({
      scale: [1, 1.5, 0.8, 1.2, 1],
      opacity: [0.7, 1, 0.8, 0.9, 0.7],
      transition: {
        duration: 4,
        ease: "easeInOut",
        times: [0, 0.25, 0.5, 0.75, 1],
        repeat: Infinity,
        repeatType: "loop",
      },
    });

    mainCircleControls.start({
      scale: [1, 1.15, 1, 1.1, 1],
      filter: [
        "drop-shadow(0 0 6px rgba(147, 51, 234, 0.7))",
        "drop-shadow(0 0 12px rgba(147, 51, 234, 0.9))",
        "drop-shadow(0 0 8px rgba(147, 51, 234, 0.7))",
        "drop-shadow(0 0 15px rgba(147, 51, 234, 0.9))",
        "drop-shadow(0 0 6px rgba(147, 51, 234, 0.7))",
      ],
      transition: {
        duration: 3,
        ease: "easeInOut",
        times: [0, 0.25, 0.5, 0.75, 1],
        repeat: Infinity,
        repeatType: "loop",
      },
    });

    brainControls.start({
      pathLength: [0, 0.5, 1, 0.8, 1],
      pathOffset: [0, 0.2, 0, 0.1, 0],
      filter: [
        "drop-shadow(0 0 1px rgba(255, 255, 255, 0.5))",
        "drop-shadow(0 0 3px rgba(255, 255, 255, 0.8))",
        "drop-shadow(0 0 2px rgba(255, 255, 255, 0.6))",
      ],
      transition: {
        duration: 10,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "loop",
      },
    });
  };

  useEffect(() => {
    startNormalAnimations();
  }, [particleControls, mainCircleControls, brainControls]);

  const createParticles = (count: number, options: { type: string }) => {
    return Array.from({ length: count }).map((_, i) => {
      const size = Math.random() * 0.3 + 0.1;
      const opacity = Math.random() * 0.5 + 0.5;

      if (options.type === "flying") {
        return (
          <motion.circle
            key={`flying-particle-${i}`}
            cx={12}
            cy={12}
            r={size}
            fill={`rgba(255, 255, 255, ${opacity})`}
            initial={{
              x: 0,
              y: 0,
              scale: 0,
              opacity: 0,
            }}
            animate={{
              x: Math.random() * 30 - 15,
              y: Math.random() * 30 - 15,
              scale: [0, 1, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut",
            }}
          />
        );
      } else if (options.type === "orbital") {
        const radius = Math.random() * 6 + 7;
        const speed = Math.random() * 15 + 10;
        const startAngle = Math.random() * 360;

        return (
          <motion.circle
            key={`orbital-particle-${i}`}
            cx={12}
            cy={12}
            r={size}
            fill={i % 2 === 0 ? "#F0EEFF" : "#E0B0FF"}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, opacity, 0],
              x: radius * Math.cos(startAngle),
              y: radius * Math.sin(startAngle),
            }}
            transition={{
              opacity: {
                duration: Math.random() * 2 + 3,
                repeat: Infinity,
                repeatType: "loop",
              },
              x: {
                duration: speed,
                repeat: Infinity,
                ease: "linear",
                repeatType: "loop",
              },
              y: {
                duration: speed,
                repeat: Infinity,
                ease: "linear",
                repeatType: "loop",
              },
            }}
            style={{
              transformBox: "fill-box",
              transformOrigin: "center",
              offsetPath: `path('M 12,${
                12 - radius
              } A ${radius},${radius} 0 1 1 12.01,${12 - radius}')`,
              offsetRotate: "0deg",
              offsetDistance: `${startAngle}%`,
            }}
          />
        );
      } else {
        return (
          <motion.circle
            key={`pulse-particle-${i}`}
            cx={12}
            cy={12}
            r={size}
            fill={i % 3 === 0 ? "#FFD700" : i % 3 === 1 ? "#E0B0FF" : "#9D4EDD"}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, opacity, 0],
              scale: [0, 2, 0],
            }}
            transition={{
              duration: Math.random() * 2 + 3,
              repeat: Infinity,
              delay: Math.random() * 3,
              repeatType: "loop",
            }}
          />
        );
      }
    });
  };

  const flyingParticles = createParticles(12, { type: "flying" });
  const orbitalParticles = createParticles(8, { type: "orbital" });
  const pulseParticles = createParticles(6, { type: "pulse" });

  const createElectricArcs = () => {
    return Array.from({ length: 4 }).map((_, i) => {
      const startAngle = i * 90;
      const radius = 8;
      const x1 = 12 + radius * Math.cos((startAngle * Math.PI) / 180);
      const y1 = 12 + radius * Math.sin((startAngle * Math.PI) / 180);
      const x2 = 12 + radius * Math.cos(((startAngle + 30) * Math.PI) / 180);
      const y2 = 12 + radius * Math.sin(((startAngle + 30) * Math.PI) / 180);

      return (
        <motion.path
          key={`electric-arc-${i}`}
          d={`M ${x1} ${y1} C ${x1 + Math.random() * 4 - 2} ${
            y1 + Math.random() * 4 - 2
          }, 
             ${x2 + Math.random() * 4 - 2} ${
            y2 + Math.random() * 4 - 2
          }, ${x2} ${y2}`}
          stroke="url(#electricGradient)"
          strokeWidth="0.4"
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{
            pathLength: [0, 1, 0],
            opacity: [0, 0.8, 0],
            d: [
              `M ${x1} ${y1} C ${x1 + Math.random() * 4 - 2} ${
                y1 + Math.random() * 4 - 2
              }, 
                ${x2 + Math.random() * 4 - 2} ${
                y2 + Math.random() * 4 - 2
              }, ${x2} ${y2}`,
              `M ${x1} ${y1} C ${x1 + Math.random() * 4 - 2} ${
                y1 + Math.random() * 4 - 2
              }, 
                ${x2 + Math.random() * 4 - 2} ${
                y2 + Math.random() * 4 - 2
              }, ${x2} ${y2}`,
              `M ${x1} ${y1} C ${x1 + Math.random() * 4 - 2} ${
                y1 + Math.random() * 4 - 2
              }, 
                ${x2 + Math.random() * 4 - 2} ${
                y2 + Math.random() * 4 - 2
              }, ${x2} ${y2}`,
            ],
          }}
          transition={{
            pathLength: {
              duration: Math.random() + 0.5,
              repeat: Infinity,
              repeatDelay: Math.random() * 2,
            },
            opacity: {
              duration: Math.random() + 0.5,
              repeat: Infinity,
              repeatDelay: Math.random() * 2,
            },
            d: {
              duration: 0.3,
              repeat: Infinity,
              repeatType: "mirror",
            },
          }}
        />
      );
    });
  };

  const electricArcs = createElectricArcs();

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <motion.circle
        cx="12"
        cy="12"
        r="11"
        stroke="url(#outerGlow)"
        strokeWidth="0.5"
        fill="none"
        animate={{
          opacity: [0.3, 0.7, 0.3],
          scale: [0.9, 1.1, 0.9],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          repeatType: "mirror",
        }}
      />

      <motion.circle
        cx="12"
        cy="12"
        r="10"
        stroke="url(#gradientCircle)"
        strokeWidth="0.8"
        fill="rgba(138, 43, 226, 0.15)"
        animate={mainCircleControls}
      />

      <motion.circle
        cx="12"
        cy="12"
        r="6"
        stroke="url(#innerGlow)"
        strokeWidth="0.3"
        fill="rgba(147, 51, 234, 0.2)"
        animate={{
          opacity: [0.5, 0.8, 0.5],
          rotate: [0, 180, 360],
        }}
        transition={{
          opacity: {
            duration: 2,
            repeat: Infinity,
            repeatType: "mirror",
          },
          rotate: {
            duration: 8,
            repeat: Infinity,
            ease: "linear",
          },
        }}
      />

      {flyingParticles}
      {orbitalParticles}
      {pulseParticles}

      <motion.g>
        <motion.path
          d="M12 7C9.5 7 8 9 8 11C8 13 9 14 10 14.5C11 15 12 15 12 16C12 17 13 17 14 17C15 17 16 16 16 14.5C16 13 15 12 14 11.5C13 11 12 10.5 12 9.5C12 8.5 13 8 14 8.5C15 9 15.5 10 15 11"
          stroke="url(#gradientBrain)"
          strokeWidth="1.5"
          strokeLinecap="round"
          fill="none"
          animate={brainControls}
        />

        <motion.path
          d="M9 10C8.5 10.5 8.3 11.3 8.5 12C8.7 12.7 9.3 13.5 10 13.8"
          stroke="url(#gradientBrainDetail)"
          strokeWidth="0.8"
          strokeLinecap="round"
          fill="none"
          animate={{
            pathLength: [0, 1, 0],
            opacity: [0, 0.8, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            repeatDelay: 1,
          }}
        />

        <motion.path
          d="M15 9C15.5 9.5 15.7 10.3 15.5 11C15.3 11.7 14.7 12.5 14 12.8"
          stroke="url(#gradientBrainDetail)"
          strokeWidth="0.8"
          strokeLinecap="round"
          fill="none"
          animate={{
            pathLength: [0, 1, 0],
            opacity: [0, 0.8, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            repeatDelay: 2,
          }}
        />
      </motion.g>

      {electricArcs}

      <motion.circle
        cx="18"
        cy="8"
        r="1"
        fill="#ffffff"
        animate={particleControls}
      />
      <motion.circle
        cx="6"
        cy="10"
        r="0.8"
        fill="#f0f0ff"
        animate={particleControls}
        custom={1}
      />
      <motion.circle
        cx="16"
        cy="18"
        r="0.7"
        fill="#ffffff"
        animate={particleControls}
        custom={2}
      />
      <motion.circle
        cx="8"
        cy="17"
        r="0.6"
        fill="#f0f0ff"
        animate={particleControls}
        custom={3}
      />

      <motion.path
        d="M12 4L12.898 6.56434L15.6085 6.90983L13.6292 8.81066L14.0534 11.5902L12 10.1L9.94658 11.5902L10.3708 8.81066L8.39155 6.90983L11.102 6.56434L12 4Z"
        fill="url(#gradientStar)"
        animate={{
          rotate: [0, 180, 360],
          scale: [1, 1.2, 1],
          transition: {
            rotate: {
              duration: 20,
              ease: "linear",
              repeat: Infinity,
            },
            scale: {
              duration: 3,
              repeat: Infinity,
              repeatType: "reverse",
            },
          },
        }}
      />

      {Array.from({ length: 12 }).map((_, i) => (
        <motion.line
          key={`ray-${i}`}
          x1="12"
          y1="12"
          x2="12"
          y2="3"
          stroke="url(#gradientRay)"
          strokeWidth="0.5"
          strokeLinecap="round"
          animate={{
            opacity: [0.3, 0.8, 0.3],
            rotate: i * 30,
            scale: [0.7, 1, 0.7],
          }}
          transition={{
            duration: 3,
            delay: i * 0.15,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          style={{ transformOrigin: "center" }}
        />
      ))}

      <motion.circle
        cx="12"
        cy="12"
        r="8"
        stroke="url(#rotatingRing)"
        strokeWidth="0.2"
        strokeDasharray="2 2"
        fill="none"
        animate={{
          rotate: [0, 360],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear",
        }}
        style={{ transformOrigin: "center" }}
      />

      <defs>
        <radialGradient
          id="outerGlow"
          cx="12"
          cy="12"
          r="12"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="rgba(255, 255, 255, 0.8)" />
          <stop offset="50%" stopColor="rgba(187, 134, 252, 0.5)" />
          <stop offset="100%" stopColor="rgba(147, 51, 234, 0)" />
        </radialGradient>

        <linearGradient
          id="gradientCircle"
          x1="2"
          y1="2"
          x2="22"
          y2="22"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#C9A0FF" />
          <stop offset="50%" stopColor="#9D4EDD" />
          <stop offset="100%" stopColor="#7B2CBF" />
        </linearGradient>

        <radialGradient
          id="innerGlow"
          cx="12"
          cy="12"
          r="6"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="70%" stopColor="#C9A0FF" />
          <stop offset="100%" stopColor="#7B2CBF" />
        </radialGradient>

        <linearGradient
          id="gradientBrain"
          x1="8"
          y1="7"
          x2="16"
          y2="17"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#F0EEFF" />
          <stop offset="50%" stopColor="#E0B0FF" />
          <stop offset="100%" stopColor="#FFFFFF" />
        </linearGradient>

        <linearGradient
          id="gradientBrainDetail"
          x1="8"
          y1="10"
          x2="15"
          y2="13"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#E0B0FF" />
        </linearGradient>

        <radialGradient
          id="gradientStar"
          cx="12"
          cy="8"
          r="5"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="70%" stopColor="#FFD700" />
          <stop offset="100%" stopColor="#FFA500" />
        </radialGradient>

        <linearGradient
          id="gradientRay"
          x1="12"
          y1="12"
          x2="12"
          y2="2"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="rgba(255, 255, 255, 0)" />
          <stop offset="100%" stopColor="rgba(255, 255, 255, 0.8)" />
        </linearGradient>

        <linearGradient
          id="rotatingRing"
          x1="12"
          y1="4"
          x2="12"
          y2="20"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="50%" stopColor="#C9A0FF" />
          <stop offset="100%" stopColor="#7B2CBF" />
        </linearGradient>

        <linearGradient
          id="electricGradient"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="0%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="rgba(255, 255, 255, 0.8)" />
          <stop offset="50%" stopColor="rgba(147, 51, 234, 0.9)" />
          <stop offset="100%" stopColor="rgba(255, 255, 255, 0.8)" />
        </linearGradient>
      </defs>
    </svg>
  );
};